import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";
import { CA_DATA } from "../../models/data.model";
import { Chart } from "chart.js";
import { SharedModule } from "../../../../shared/shared.module";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { WidgetPeriodFiltersComponent } from "../../../../shared/components/widget-period-filters/widget-period-filters.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { toSignal } from "@angular/core/rxjs-interop";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { ActivityInfosService } from "../../../activity_infos/services/activity_infos.service";
import {
  CaNMoinsDialogComponent,
  CaNMoinsDialogData,
  CaNMoinsPeriodScale,
} from "./ca-nmoins-dialog.component";

@Component({
  selector: "vl-widget-ca-nmoins",
  standalone: true,
  imports: [
    SharedModule,
    WidgetTitleComponent,
    WidgetPeriodFiltersComponent,
    ChipsComponent,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-ca-nmoins.component.html",
  styleUrl: "./widget-ca-nmoins.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetCaNMoinsComponent {
  data = input<CA_DATA>();
  metadata = input<EntityMetadata>();
  widget = input<Widget>();
  elementRef = inject(ElementRef);
  private dialogsService = inject(DialogsService);
  private activityInfos = toSignal(inject(ActivityInfosService).currentActivityInfos$);

  period = signal<string>("m");
  timeScale = signal<string>("1 a");
  comparison = signal<string | null>("n-1");

  canvasRef = viewChild<ElementRef>("canmoins");
  chart: Chart;

  scaleLabelPlural = computed(() => {
    switch (this.period()) {
      case "j": return "jours";
      case "s": return "semaines";
      case "m": return "mois";
      case "a": return "années";
      default: return "périodes";
    }
  });

  private timeScaleLabelMap: Record<string, string> = {
    "1 j": "Dernier jour",
    "5 j": "5 derniers jours",
    "1 m": "Dernier mois",
    "6 m": "6 derniers mois",
    "YTD": "Depuis le 1er janvier",
    "EX": "Exercice en cours",
    "1 a": "12 derniers mois",
    "5 a": "5 dernières années",
    "Max": "Tout l'historique",
  };

  private get exerciceStart(): string | undefined {
    return this.activityInfos()?.dateDebutExercice ?? undefined;
  }

  openFullList(): void {
    const raw = this.data()?.data;
    if (!raw?.length) return;

    const dialogData: CaNMoinsDialogData = {
      rawData: raw,
      initialPeriod: this.period() as CaNMoinsPeriodScale,
      timeScale: this.timeScale(),
      timeScaleLabel: this.timeScaleLabelMap[this.timeScale()] ?? this.timeScale(),
      exerciceStart: this.exerciceStart,
    };

    this.dialogsService.open(
      CaNMoinsDialogComponent,
      "min(95vw, 960px)",
      "fit-content",
      dialogData,
    );
  }

  constructor() {
    effect(
      () => {
        const data = this.data();
        const canvas = this.canvasRef();
        this.period();
        this.timeScale();
        this.comparison();

        if (data && canvas) {
          this.buildChart();
        }
      },
      { allowSignalWrites: true },
    );
  }

  buildChart() {
    if (this.data()?.data.length > 0) {
      const result = WidgetHelperService.getCaPeriodeComparisonChart(
        this.data()?.data,
        {
          timeScale: this.timeScale(),
          period: this.period(),
          comparison: this.comparison() ?? "n-1",
          exerciceStart: this.exerciceStart,
        },
      );

      const styles = getComputedStyle(this.elementRef.nativeElement);
      const bgAlt = styles.getPropertyValue("--color-on-background-alt");
      const colorN = styles.getPropertyValue("--color-on-accent-alt");
      const colorNMoins1 = styles.getPropertyValue("--color-on-accent");

      const datasets: any[] = [
        {
          label: "N",
          data: result.current,
          borderColor: colorN,
          backgroundColor: colorN + "20",
          pointBackgroundColor: colorN,
          pointBorderColor: colorN,
          tension: 0,
          fill: true,
        },
      ];

      if (result.comparison.length > 0) {
        datasets.push({
          label: "N-1",
          data: result.comparison,
          borderColor: colorNMoins1,
          backgroundColor: "transparent",
          pointBackgroundColor: colorNMoins1,
          pointBorderColor: colorNMoins1,
          borderDash: [4, 4],
          tension: 0,
        });
      }

      const compLabels = result.comparisonLabels;
      const canvas = this.canvasRef().nativeElement;
      const ctx = canvas.getContext("2d");

      if (this.chart) {
        this.chart.destroy();
      }

      this.chart = new Chart(ctx, {
        type: "line",
        data: { datasets, labels: result.labels },
        options: {
          plugins: {
            legend: {
              display: true,
              labels: {
                color: bgAlt,
                boxWidth: 12,
                padding: 8,
                font: { size: 11 },
              },
            },
            tooltip: {
              callbacks: {
                title: (items) => {
                  if (!items.length) return "";
                  const idx = items[0].dataIndex;
                  const currentLabel = result.labels[idx] ?? "";
                  if (compLabels[idx]) {
                    return `${currentLabel} vs ${compLabels[idx]}`;
                  }
                  return currentLabel;
                },
                label: (context) => {
                  const value = context.parsed.y ?? 0;
                  return `${context.dataset.label}: ${WidgetHelperService.formatNumeric(value)}€`;
                },
              },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: {
                color: bgAlt,
                maxRotation: 45,
                font: { size: 10 },
              },
            },
            y: {
              ticks: {
                color: bgAlt,
                font: { size: 10 },
                callback: (value) =>
                  WidgetHelperService.formatNumeric(+value) + "€",
              },
            },
          },
        },
      });
    }
  }
}
